<template>
  <Modal :is-open="isOpen" :title="t('recording.recovery.title')" @close="handleClose">
    <div v-if="isLoading" class="recovery-dialog__loading">
      {{ t('common.loading.default') }}
    </div>

    <div v-else-if="sessions.length === 0" class="recovery-dialog__empty">
      {{ t('recording.recovery.noSessions') }}
    </div>

    <div v-else class="recovery-dialog__content">
      <p class="recovery-dialog__intro">
        {{ t('recording.recovery.intro') }}
      </p>

      <ul class="recovery-dialog__list">
        <li
          v-for="recoverable in sessions"
          :key="recoverable.session.id"
          class="recovery-dialog__item"
        >
          <div class="recovery-dialog__session-info">
            <div class="recovery-dialog__session-header">
              <span class="recovery-dialog__icon">&#128197;</span>
              <span class="recovery-dialog__date">{{ recoverable.formattedDate }}</span>
            </div>
            <div class="recovery-dialog__session-details">
              <span class="recovery-dialog__icon">&#9201;</span>
              <span class="recovery-dialog__duration">
                {{ t('recording.recovery.duration', { minutes: recoverable.durationMinutes }) }}
              </span>
            </div>
            <div v-if="recoverable.appointmentName" class="recovery-dialog__session-details">
              <span class="recovery-dialog__icon">&#128100;</span>
              <span class="recovery-dialog__appointment">{{ recoverable.appointmentName }}</span>
            </div>
          </div>

          <div class="recovery-dialog__actions">
            <Button
              variant="primary"
              size="small"
              :loading="uploadingSessionId === recoverable.session.id"
              :disabled="isProcessing || !canUpload(recoverable.session)"
              :title="canUpload(recoverable.session) ? undefined : t('recording.recovery.uploadRequiresLink')"
              @click="handleUpload(recoverable.session.id)"
            >
              {{ t('recording.recovery.upload') }}
            </Button>
            <Button
              v-if="recoverable.isResumable && recoverable.session.appointmentId"
              variant="secondary"
              size="small"
              :disabled="isProcessing"
              @click="handleResume(recoverable.session.id)"
            >
              {{ t('recording.recovery.resume') }}
            </Button>
            <Button
              variant="danger"
              size="small"
              :loading="discardingSessionId === recoverable.session.id"
              :disabled="isProcessing"
              @click="handleDiscard(recoverable.session.id)"
            >
              {{ t('recording.recovery.discard') }}
            </Button>
          </div>
        </li>
      </ul>
    </div>

    <template #footer>
      <Button variant="secondary" @click="handleClose">
        {{ t('common.close') }}
      </Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import Modal from '@/components/organisms/Modal.vue'
import Button from '@/components/atoms/Button.vue'
import type { RecoverableSession } from '@/composables/useSessionRecovery'
import type { RecordingSession } from '@/services/RecordingDatabase'

interface Props {
  isOpen: boolean
  sessions: RecoverableSession[]
  isLoading?: boolean
  /**
   * Uploads the recording. Function props rather than events because the dialog
   * awaits them to keep the per-row spinner up until the request finishes, and
   * `emit` returns undefined (#3178).
   */
  onUpload: (sessionId: string) => Promise<void>
  /** Discards the recording and its local audio. */
  onDiscard: (sessionId: string) => Promise<void>
}

interface Emits {
  (e: 'close'): void
  (e: 'resume', sessionId: string): void
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
})

const emit = defineEmits<Emits>()

const { t } = useI18n()

const uploadingSessionId = ref<string | null>(null)
const discardingSessionId = ref<string | null>(null)

const isProcessing = computed(() => {
  return uploadingSessionId.value !== null || discardingSessionId.value !== null
})

/**
 * A recording can be uploaded once it is owned by an appointment or a form
 * submission (the transcript is linked to exactly one of them).
 */
function canUpload(session: RecordingSession): boolean {
  return Boolean(session.appointmentId || session.formSubmissionId)
}

function handleClose() {
  if (!isProcessing.value) {
    emit('close')
  }
}

async function handleUpload(sessionId: string) {
  uploadingSessionId.value = sessionId
  try {
    await props.onUpload(sessionId)
  } finally {
    uploadingSessionId.value = null
  }
}

function handleResume(sessionId: string) {
  emit('resume', sessionId)
}

async function handleDiscard(sessionId: string) {
  discardingSessionId.value = sessionId
  try {
    await props.onDiscard(sessionId)
  } finally {
    discardingSessionId.value = null
  }
}
</script>

<style scoped>
.recovery-dialog__loading,
.recovery-dialog__empty {
  padding: var(--spacing-xl);
  text-align: center;
  color: var(--color-text-secondary);
}

.recovery-dialog__content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.recovery-dialog__intro {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

.recovery-dialog__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.recovery-dialog__item {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  padding: var(--spacing-lg);
  background: var(--color-background-secondary);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-default);
}

.recovery-dialog__session-info {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.recovery-dialog__session-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.recovery-dialog__session-details {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

.recovery-dialog__icon {
  font-size: var(--font-size-base);
}

.recovery-dialog__date {
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

.recovery-dialog__duration,
.recovery-dialog__appointment {
  color: var(--color-text-secondary);
}

.recovery-dialog__actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-sm);
}

@media (--breakpoint-xs-up) {
  .recovery-dialog__item {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  .recovery-dialog__actions {
    flex-shrink: 0;
  }
}
</style>
